import React, { type FC, useCallback, useMemo, useState } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Button, Stack, Typography, useTheme } from '@mui/material';
import _ from 'lodash';
import { type QueryParamConfigMap, StringParam } from 'serialize-query-params';

import {
  FolderType,
  getFilterTalentsQueryKey,
  getGetProcessTalentsQueryKey,
  ProcessResponseDTO,
  ProcessResponseDTOStatus,
  ProcessUpdateDtoAssessmentType,
  TalentCreateDTOType,
  TalentListRequestDTO,
  TalentProcessResponseDTO,
  TalentsCriteriaDTOType,
  useFilterTalents
} from '@/api/generated';
import PlusIcon from '@/assets/icons/plus.svg?react';
import ResetIcon from '@/assets/icons/reset.svg?react';
import EmptyState from '@/components/EmptyState';
import Field from '@/components/fields/Field';
import { getFolderSlugForLocation } from '@/components/fields/Location/locationTreeUtils';
import SearchInput from '@/components/fields/SearchInput';
import Form from '@/components/Form/Form';
import Loader from '@/components/Loader/Loader';
import ProcessCreateTalentModal from '@/containers/Process/ProcessCreateTalentModal';
import TalentCard from '@/containers/Process/TalentCard';
import { useFolderPermissions } from '@/hooks/permissions/useFolderPermissions';
import { useProcessPermissions } from '@/hooks/permissions/useProcessPermissions';
import { DEFAULT_PAGINATION, DEFAULT_SORTING, useEntityList } from '@/hooks/useEntityList';
import { FieldPrefixProvider, useFieldPrefix } from '@/hooks/useFieldPrefix';
import { useOrganizationStructure } from '@/hooks/useOrganizationStructure';
import { ReadonlyProvider } from '@/hooks/useReadonly';
import { toNewTalent } from '@/root/linker';
import typography from '@/theme/typography';

interface TalentsTabProps {
  processId: string;
  process?: ProcessResponseDTO;
  isCreateFlow?: boolean;
  showTalentContactInfo?: boolean;
}

const TALENT_ADD_ALLOWED_STATUSES: (ProcessResponseDTOStatus | undefined)[] = [
  ProcessResponseDTOStatus.UNPUBLISHED,
  ProcessResponseDTOStatus.ACTIVE,
  ProcessResponseDTOStatus.ON_HOLD
];

const isValidStatus = (status: ProcessResponseDTOStatus | undefined) => TALENT_ADD_ALLOWED_STATUSES.includes(status);

const TalentsTab: FC<TalentsTabProps> = ({ process, isCreateFlow, showTalentContactInfo }) => {
  const { palette, spacing } = useTheme();
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();
  const { mapStructure, location } = useOrganizationStructure(process?.location?.slug);
  const [queryBody, setQueryBody] = useState<TalentListRequestDTO | null>(null);
  const [showCreateTalentModal, setShowCreateTalentModal] = useState(false);

  const { watch, setValue } = useFormContext();
  const { getPrefixedFieldName } = useFieldPrefix();

  const talentsToAdd = watch(getPrefixedFieldName('talentsToAdd'));

  const processId = process?.id;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updatedTalentIdsSet = useMemo(() => new Set(talentsToAdd), []);

  const { value: { canAddTalents } = {} } = useProcessPermissions({
    variables: { processId: processId! }
  });

  const { value: { canCreateTalents: canCreateTalentsInFolder } = {} } = useFolderPermissions({
    variables: {
      organizationSlug: process?.organization?.slug,
      locationSlug: process?.location?.slug,
      locationType: process?.location?.type
    }
  });

  const addingTalentsAllowed = useMemo(
    () => canAddTalents && isValidStatus(process?.status),
    [process?.status, canAddTalents]
  );

  const updateTalentsFormValue = useCallback(
    () =>
      setValue(getPrefixedFieldName('talentsToAdd'), Array.from(updatedTalentIdsSet), {
        shouldDirty: true,
        shouldTouch: true
      }),
    [getPrefixedFieldName, setValue, updatedTalentIdsSet]
  );

  const handleAddTalent = useCallback(
    (talentId: string) => {
      if (addingTalentsAllowed) {
        updatedTalentIdsSet.add(talentId);
        updateTalentsFormValue();
      }
    },
    [addingTalentsAllowed, updateTalentsFormValue, updatedTalentIdsSet]
  );

  const handleRemoveTalent = useCallback(
    (talentId: string) => {
      if (addingTalentsAllowed) {
        updatedTalentIdsSet.delete(talentId);
        updateTalentsFormValue();
      }
    },
    [addingTalentsAllowed, updateTalentsFormValue, updatedTalentIdsSet]
  );

  const resolveType = useCallback((assessmentType?: ProcessUpdateDtoAssessmentType) => {
    switch (assessmentType) {
      case ProcessUpdateDtoAssessmentType.INTERNAL:
        return TalentCreateDTOType.EMPLOYEE;
      case ProcessUpdateDtoAssessmentType.EXTERNAL:
        return TalentCreateDTOType.CANDIDATE;
      case ProcessUpdateDtoAssessmentType.ANY:
      default:
        return undefined;
    }
  }, []);

  const handleOpenCreateTalentModal = useCallback(() => {
    if (isCreateFlow) {
      setShowCreateTalentModal(true);

      return;
    }

    window.open(
      toNewTalent({
        type: resolveType(process?.assessmentType),
        organizationSlug: process?.organization?.slug,
        locationSlug: getFolderSlugForLocation(process?.location, mapStructure)
      }),
      // eslint-disable-next-line i18next/no-literal-string
      '_blank'
    );
  }, [
    isCreateFlow,
    mapStructure,
    process?.assessmentType,
    process?.location,
    process?.organization?.slug,
    resolveType
  ]);

  const handleCloseCreateTalentModal = useCallback(() => {
    setShowCreateTalentModal(false);
  }, []);

  const handleTalentCreated = useCallback(
    (talentId: string) => {
      if (addingTalentsAllowed) {
        updatedTalentIdsSet.add(talentId);
        updateTalentsFormValue();
      }

      if (process?.id) {
        queryClient.invalidateQueries(getGetProcessTalentsQueryKey(process?.id));

        if (queryBody) {
          queryClient.invalidateQueries(getFilterTalentsQueryKey(queryBody));
        }
      }
    },
    [addingTalentsAllowed, updatedTalentIdsSet, updateTalentsFormValue, queryClient, process?.id, queryBody]
  );

  const { data: orgTalentsResponse, isLoading } = useFilterTalents(
    queryBody ?? {},
    { includeStats: !queryBody?.page },
    {
      query: { suspense: false, enabled: !!queryBody }
    }
  );
  const { talents = [] } = orgTalentsResponse || {};

  const processLocation = useMemo(() => process?.location?.slug || process?.organization?.slug, [process]);

  const talentType: TalentsCriteriaDTOType | undefined = useMemo(() => {
    switch (process?.assessmentType) {
      case ProcessUpdateDtoAssessmentType.INTERNAL:
        return TalentsCriteriaDTOType.EMPLOYEE;
      case ProcessUpdateDtoAssessmentType.EXTERNAL:
        return TalentsCriteriaDTOType.CANDIDATE;
      case ProcessUpdateDtoAssessmentType.ANY:
      default:
        return undefined;
    }
  }, [process?.assessmentType]);

  const parentFolder = useMemo(() => {
    let currentLocation = location;
    while (currentLocation?.type !== FolderType.FOLDER && !!currentLocation?.parentSlug) {
      currentLocation = mapStructure?.[currentLocation.parentSlug!];
    }

    return currentLocation;
  }, [location, mapStructure]);

  const defaultValues: TalentListRequestDTO = useMemo(
    () =>
      _.omitBy(
        {
          ...DEFAULT_VALUES,
          size: 100,
          talents: {
            organization: process?.organization?.slug,
            type: talentType,
            locations: [
              {
                slug: parentFolder?.slug,
                type: parentFolder?.type
              }
            ],
            excludeProcessIds: [processId!]
          }
        } satisfies TalentListRequestDTO,
        _.isUndefined
      ),
    [parentFolder?.slug, parentFolder?.type, process?.organization?.slug, processId, talentType]
  );

  const {
    form: filterForm
    // , handleChangeSort, handleChangePagination, sorting, pagination
  } = useEntityList<FieldValues, TalentListRequestDTO>(defaultValues, MAP_CONFIG, setQueryBody, undefined, 300);

  const isEmpty = !talents.length;

  const canCreateTalent = addingTalentsAllowed && !!processLocation && !!process && canCreateTalentsInFolder;

  const isFilterEmpty = useMemo(() => _.isEqual(queryBody, defaultValues), [queryBody, defaultValues]);

  const handleResetFilters = useCallback(() => {
    filterForm.reset();
  }, [filterForm]);

  return (
    <FieldPrefixProvider prefix="">
      <Stack gap={1} p={2} height="100%">
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Form form={filterForm} sx={{ justifyContent: 'start', display: 'flex' }}>
            <ReadonlyProvider readonly={false}>
              <Field
                component={SearchInput}
                name="talents.searchByName"
                innerProps={{
                  sx: { minWidth: 320, bgcolor: 'background.default' },
                  placeholder: t('processSettings.form.talents.searchTalentsPlaceholder')
                }}
              />
            </ReadonlyProvider>
          </Form>
          {canCreateTalent && (
            <Button
              variant="outlined"
              onClick={handleOpenCreateTalentModal}
              sx={{
                ...typography.body2,
                fontWeight: typography.fontWeightMedium,
                py: 0.5,
                paddingInlineStart: 1,
                paddingInlineEnd: 1.5,
                '& .MuiButton-icon': {
                  marginInlineStart: 0,
                  marginInlineEnd: 1
                },
                '& .PlusIcon-root': { width: spacing(2), height: spacing(2) }
              }}
              startIcon={<PlusIcon className="PlusIcon-root" />}
            >
              {t('processSettings.form.talents.addTalent')}
            </Button>
          )}
        </Stack>
        <>
          {(isLoading || !queryBody) && (
            <Stack justifyContent="center" alignItems="center" height="100%">
              <Loader />
            </Stack>
          )}
          {!isLoading && !!queryBody && isEmpty && isFilterEmpty && (
            <Stack justifyContent="center" height="100%">
              <EmptyState
                imageUrl="/images/empty-talent-list-illustration.svg"
                imageAlt={t('processSettings.form.talents.noTalentsTitle')}
                imageSx={{ width: 243 }}
                onCreate={canCreateTalent ? handleOpenCreateTalentModal : undefined}
                buttonLabel={canCreateTalent ? t('processSettings.form.talents.addTalent') : undefined}
              >
                <Stack alignItems="center">
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: typography.fontWeightMedium, color: palette.grey[700], mb: 0 }}
                  >
                    {t('processSettings.form.talents.noTalentsTitle')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: palette.grey[700] }}>
                    {t('processSettings.form.talents.noTalentsDescription')}
                  </Typography>
                </Stack>
              </EmptyState>
            </Stack>
          )}
          {!isLoading && !!queryBody && isEmpty && !isFilterEmpty && (
            <Stack justifyContent="center" height="100%">
              <EmptyState
                imageUrl="/images/empty-talent-list-illustration.svg"
                imageAlt={t('processSettings.form.talents.noTalentsSearch')}
                imageSx={{ width: 243 }}
                onCreate={handleResetFilters}
                buttonLabel={t('processSettings.form.talents.resetFilters')}
                buttonIcon={<ResetIcon />}
              >
                <Stack alignItems="center">
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: typography.fontWeightMedium, color: palette.grey[700], mb: 0 }}
                  >
                    {t('processSettings.form.talents.noTalentsSearch')}
                  </Typography>
                </Stack>
              </EmptyState>
            </Stack>
          )}
          {!isLoading &&
            !isEmpty &&
            process &&
            talents?.map(talent => (
              <TalentCard
                key={talent.id}
                talent={talent as TalentProcessResponseDTO}
                process={process}
                showAddButton={!updatedTalentIdsSet.has(talent.id) && addingTalentsAllowed}
                showRemoveButton={updatedTalentIdsSet.has(talent.id) && addingTalentsAllowed}
                showTestSessionInfo={false}
                showProgress={false}
                showTalentContactInfo={showTalentContactInfo}
                onAdd={handleAddTalent}
                onRemove={handleRemoveTalent}
              />
            ))}
        </>

        {showCreateTalentModal && process && (
          <ProcessCreateTalentModal
            onClose={handleCloseCreateTalentModal}
            process={process}
            onTalentCreated={handleTalentCreated}
            queryBody={queryBody!}
          />
        )}
      </Stack>
    </FieldPrefixProvider>
  );
};

const DEFAULT_VALUES = {
  ...DEFAULT_SORTING,
  ...DEFAULT_PAGINATION
} satisfies TalentListRequestDTO;

const MAP_CONFIG = {
  query: StringParam
} satisfies QueryParamConfigMap;

export default TalentsTab;
